import { consumeVoiceInputFlag, playTurnAudio } from "./voice-chat-experience.js";
import { assertFrontendConfig, config } from "./config.js";
import { publishProgressiveFlightPlanResponse } from "./progressive-flight-plan.js";
import { appendConversationTurn, archiveActiveConversation, consumeResume, getOrCreateVisitorId, initializeVisitorContinuity, migrateConversationSession, previousConversationSummary, saveConversation, updateLastResponse } from "./visitor-continuity.js";
import type { AddonOffer, BusinessPath, ContactIdentity, DiscoveryResponse, HumanHandoffResponse, NovaConversationTurn } from "./types.js";

let activeDiscoverySessionId = "";
let activeDiscoveryPath: BusinessPath | undefined;

interface VisitorLocation { city: string | null; region: string | null; regionCode: string | null; country: string | null; }
const NULL_VISITOR_LOCATION: VisitorLocation = { city: null, region: null, regionCode: null, country: null };

/**
 * Fetched once per page load from this site's own /api/geo (a Cloudflare
 * Pages Function - same origin, NOT the Railway API, so it deliberately
 * bypasses request()/get() and their novaApiBaseUrl prefix). Silent: no
 * loading state, and any failure (network error, non-2xx, malformed JSON,
 * local dev where the Function doesn't run) resolves to all-null rather than
 * rejecting - the backend already treats all-null as "ask the visitor
 * directly" rather than as an error.
 */
let visitorLocationPromise: Promise<VisitorLocation> | undefined;
function fetchVisitorLocation(): Promise<VisitorLocation> {
  if (!visitorLocationPromise) {
    visitorLocationPromise = fetch("/api/geo")
      .then((response) => (response.ok ? response.json() as Promise<Partial<VisitorLocation>> : NULL_VISITOR_LOCATION))
      .then((geo) => ({
        city: typeof geo.city === "string" ? geo.city : null,
        region: typeof geo.region === "string" ? geo.region : null,
        regionCode: typeof geo.regionCode === "string" ? geo.regionCode : null,
        country: typeof geo.country === "string" ? geo.country : null,
      }))
      .catch(() => NULL_VISITOR_LOCATION);
  }
  return visitorLocationPromise;
}

interface DiscoveryResumeEnvelope {
  state: { path: BusinessPath; answers: Record<string, unknown>; completed: boolean; meaningfulTurns?: number };
  version: number;
  response: DiscoveryResponse;
}

interface NovaConversationEnvelope extends NovaConversationTurn {
  completed?: boolean;
  tier?: DiscoveryResponse["tier"];
  progress?: DiscoveryResponse["progress"];
  view?: DiscoveryResponse["view"];
  progressiveFlightPlan?: DiscoveryResponse["progressiveFlightPlan"];
  result?: DiscoveryResponse["result"];
  websiteBuildResult?: DiscoveryResponse["websiteBuildResult"];
  ghlSaasResult?: DiscoveryResponse["ghlSaasResult"];
  conversationTurn?: NovaConversationTurn;
}

interface SaveFlightPlanResponse { status: string; answer: string; }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  assertFrontendConfig();
  const response = await fetch(`${config.novaApiBaseUrl}${path}`, init);
  const text = await response.text();
  let payload: Record<string, unknown> = {};
  try { payload = text ? JSON.parse(text) as Record<string, unknown> : {}; } catch { payload = {}; }
  if (!response.ok) {
    const detail = typeof payload.detail === "string" ? payload.detail : undefined;
    const title = typeof payload.title === "string" ? payload.title : undefined;
    const error = new Error(detail ?? title ?? `Nova request failed (${response.status})`);
    Object.assign(error, { status: response.status });
    throw error;
  }
  return payload as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}
function get<T>(path: string): Promise<T> { return request<T>(path); }

/** Tells the save form which add-ons Nova just named. It only highlights them; the visitor still ticks them. */
function publishAddonSuggestions(ids: string[] | undefined): void {
  if (ids && ids.length > 0) window.dispatchEvent(new CustomEvent("nova:addon-suggestions", { detail: ids }));
}

export function getAddonOffers(): Promise<{ addons: AddonOffer[] }> { return get<{ addons: AddonOffer[] }>("/v1/discovery/addon-offers"); }

function publish(response: DiscoveryResponse): DiscoveryResponse {
  publishProgressiveFlightPlanResponse(response);
  window.dispatchEvent(new CustomEvent("nova:conversation-state", { detail: response }));
  publishAddonSuggestions(response.conversationTurn?.suggestedAddonIds);
  playTurnAudio(response.conversationTurn?.audio);
  if (response.completed && response.result) window.dispatchEvent(new CustomEvent("nova:flight-plan", { detail: response.result.flightPlan }));
  if (response.completed && response.websiteBuildResult) window.dispatchEvent(new CustomEvent("nova:website-build-result", { detail: response.websiteBuildResult }));
  if (response.completed && response.ghlSaasResult) window.dispatchEvent(new CustomEvent("nova:ghl-saas-result", { detail: response.ghlSaasResult }));
  if (response.humanHandoff) window.dispatchEvent(new CustomEvent("nova:human-handoff", { detail: response.humanHandoff }));
  return response;
}

async function createServerConversation(sessionId: string, path: BusinessPath): Promise<DiscoveryResponse> {
  return post<DiscoveryResponse>(`/v1/discovery/${encodeURIComponent(sessionId)}/start`, {
    path,
    visitorId: getOrCreateVisitorId(),
    conversationId: sessionId,
    previousConversationSummary: previousConversationSummary(),
    visitorLocation: await fetchVisitorLocation(),
  });
}

async function rehydrateConversation(sessionId: string, path: BusinessPath, answers: Record<string, string | number | boolean>): Promise<DiscoveryResponse> {
  let response = await createServerConversation(sessionId, path);
  for (const [field, value] of Object.entries(answers)) {
    if (response.completed) break;
    const candidate = await post<DiscoveryResponse>(`/v1/discovery/${encodeURIComponent(sessionId)}/answers`, { field, value, visitorId: getOrCreateVisitorId() });
    if (!candidate.clarification) response = candidate;
  }
  return response;
}

export async function startDiscovery(sessionId: string, path: BusinessPath): Promise<DiscoveryResponse> {
  const resumable = consumeResume(path);
  if (resumable) {
    activeDiscoverySessionId = resumable.sessionId;
    activeDiscoveryPath = resumable.path;
    try {
      const restored = await get<DiscoveryResumeEnvelope>(`/v1/discovery/${encodeURIComponent(resumable.sessionId)}`);
      saveConversation(resumable.sessionId, resumable.path, restored.response);
      return publish(restored.response);
    } catch (error) {
      const statusCode = typeof error === "object" && error !== null && "status" in error ? Number((error as { status?: unknown }).status) : 0;
      if (statusCode !== 404) throw error;
      activeDiscoverySessionId = sessionId;
      activeDiscoveryPath = path;
      const rebuilt = await rehydrateConversation(sessionId, path, resumable.answers);
      migrateConversationSession(resumable, sessionId, rebuilt);
      return publish(rebuilt);
    }
  }

  archiveActiveConversation();
  activeDiscoverySessionId = sessionId;
  activeDiscoveryPath = path;
  const response = await createServerConversation(sessionId, path);
  saveConversation(sessionId, path, response);
  return publish(response);
}

export async function answerDiscovery(sessionId: string, field: string, value: string | number | boolean, _identity?: ContactIdentity, voiceInput = false): Promise<DiscoveryResponse> {
  const effectiveSessionId = activeDiscoverySessionId || sessionId;
  const response = await post<DiscoveryResponse>(`/v1/discovery/${encodeURIComponent(effectiveSessionId)}/answers`, { field, value, visitorId: getOrCreateVisitorId(), ...(voiceInput ? { voiceInput: true } : {}) });
  if (activeDiscoveryPath) saveConversation(effectiveSessionId, activeDiscoveryPath, response, { field, value });
  return publish(response);
}

export async function askNova(question: string, voiceInput = false): Promise<NovaConversationTurn> {
  if (!activeDiscoverySessionId) throw new Error("Nova's discovery session is not active.");
  const envelope = await post<NovaConversationEnvelope>(`/v1/discovery/${encodeURIComponent(activeDiscoverySessionId)}/conversation`, { question, visitorId: getOrCreateVisitorId(), ...(voiceInput ? { voiceInput: true } : {}) });
  const turn: NovaConversationTurn = envelope.conversationTurn
    ? { ...envelope.conversationTurn, ...(envelope.humanHandoff ? { humanHandoff: envelope.humanHandoff } : {}) }
    : { answer: envelope.answer, mode: envelope.mode, intent: envelope.intent, ...(envelope.audio ? { audio: envelope.audio } : {}), ...(envelope.humanHandoff ? { humanHandoff: envelope.humanHandoff } : {}) };
  appendConversationTurn(question, turn.answer);
  publishAddonSuggestions(envelope.suggestedAddonIds ?? envelope.conversationTurn?.suggestedAddonIds);
  if (envelope.completed !== undefined && envelope.progress && envelope.view && envelope.progressiveFlightPlan) {
    const activePath = activeDiscoveryPath;
    const response = {
      path: activePath ?? "existing_business",
      completed: envelope.completed,
      ...(envelope.tier ? { tier: envelope.tier } : {}),
      progress: envelope.progress,
      view: envelope.view,
      progressiveFlightPlan: envelope.progressiveFlightPlan,
      ...(envelope.result ? { result: envelope.result } : {}),
      ...(envelope.websiteBuildResult ? { websiteBuildResult: envelope.websiteBuildResult } : {}),
      ...(envelope.ghlSaasResult ? { ghlSaasResult: envelope.ghlSaasResult } : {}),
      conversationTurn: turn,
    } as DiscoveryResponse;
    updateLastResponse(response);
    publish(response); // plays turn.audio via publish()'s own check - do not also play it below
  } else {
    playTurnAudio(turn.audio);
  }
  if (turn.humanHandoff) window.dispatchEvent(new CustomEvent("nova:human-handoff", { detail: turn.humanHandoff }));
  return turn;
}

export function saveFlightPlan(identity: ContactIdentity): Promise<SaveFlightPlanResponse> {
  if (!activeDiscoverySessionId) return Promise.reject(new Error("Nova's discovery session is not active."));
  return post<SaveFlightPlanResponse>(`/v1/discovery/${encodeURIComponent(activeDiscoverySessionId)}/save-flight-plan`, { identity, visitorId: getOrCreateVisitorId() });
}

export interface LaunchPlanCheckoutSession {
  url: string;
}

/** Reuses the same identity already collected by the Save Flight Plan form - Stripe Checkout gets a real Customer (name + email) from it rather than asking the visitor to type their details again. */
export function createLaunchPlanCheckout(identity: ContactIdentity, addonItemIds: string[] = []): Promise<LaunchPlanCheckoutSession> {
  if (!activeDiscoverySessionId) return Promise.reject(new Error("Nova's discovery session is not active."));
  // Always send the list (even empty): it is exactly what the visitor ticked, so the server never falls back to anything they didn't confirm.
  return post<LaunchPlanCheckoutSession>(`/v1/discovery/${encodeURIComponent(activeDiscoverySessionId)}/create-checkout-session`, { identity, addonItemIds, visitorId: getOrCreateVisitorId() });
}

export function completeHumanHandoff(identity: ContactIdentity, requestText: string): Promise<HumanHandoffResponse> {
  if (!activeDiscoverySessionId) return Promise.reject(new Error("Nova's discovery session is not active."));
  return post<HumanHandoffResponse>(`/v1/discovery/${encodeURIComponent(activeDiscoverySessionId)}/handoff`, { identity, requestText, visitorId: getOrCreateVisitorId() });
}

export interface LaunchPlanStatus {
  remainingFoundingSlots: number;
  foundingSetupFeeUsd: number;
  standardSetupFeeUsd: number;
  monthlyFeeUsd: number;
}

/** Not gated on an active discovery session - the homepage offer section calls this before Nova has even started. Callers should degrade gracefully (e.g. static copy) since this reports 503 wherever launch-plan tracking isn't configured. */
export function getLaunchPlanStatus(): Promise<LaunchPlanStatus> {
  return get<LaunchPlanStatus>("/v1/launch-plan/status");
}

export interface OptInSubmission {
  phone: string;
  /** Consent to transactional messages (lead follow-up, appointment reminders). Tracked separately from marketingOptIn per TCR's consent-separation requirement. */
  transactionalOptIn: boolean;
  /** Consent to marketing/promotional messages - a second, independent opt-in. */
  marketingOptIn: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface OptInSubmissionResponse {
  status: "dry_run" | "confirmed";
  message: string;
}

export function submitOptIn(input: OptInSubmission): Promise<OptInSubmissionResponse> {
  return post<OptInSubmissionResponse>("/v1/opt-in", input);
}

export interface ContactFormSubmission {
  name: string;
  email: string;
  message: string;
  phone?: string;
}

export interface ContactFormResponse {
  status: "dry_run" | "confirmed";
  answer: string;
}

export function submitContactForm(input: ContactFormSubmission): Promise<ContactFormResponse> {
  return post<ContactFormResponse>("/v1/contact", input);
}

document.addEventListener("nova:complete-human-handoff", (event) => {
  const detail = (event as CustomEvent<{ identity: ContactIdentity; requestText: string }>).detail;
  if (!detail?.identity?.email || !detail.requestText) return;
  void completeHumanHandoff(detail.identity, detail.requestText)
    .then((response) => window.dispatchEvent(new CustomEvent("nova:human-handoff-complete", { detail: response })))
    .catch((error: unknown) => window.dispatchEvent(new CustomEvent("nova:human-handoff-error", { detail: { message: error instanceof Error ? error.message : "Moonrock could not complete the handoff right now." } })));
});

const resourceQuestions: Record<string, string> = {
  pricing: "Can you explain the pricing for the recommendation in my Flight Plan?",
  payments: "What payment options or payment timing can we discuss?",
  implementation: "How would implementation work for the business we just discussed?",
  local: "What does working with Moonrock as a local partner look like?",
  services: "What other Moonrock capabilities are relevant to the problems in my Flight Plan?",
};

function conversationAnswerTarget(): HTMLDivElement | null { return document.querySelector<HTMLDivElement>("#resource-answer"); }

function isReadySignal(question: string): boolean {
  return /\block (it|this) in\b|\bsign (me|us) up\b|\blet'?s (do this|move forward|get started|proceed|go)\b|\bready to (move forward|proceed|start|go|sign up)\b|\bi'?m ready\b|\bhow do i (get started|sign up|proceed)\b/i.test(question);
}

async function renderRuntimeConversation(question: string, voiceInput = false): Promise<void> {
  const target = conversationAnswerTarget();
  if (!target) return;
  target.textContent = "Nova is thinking about that in the context of your Flight Plan…";
  window.dispatchEvent(new CustomEvent("nova:voice-state", { detail: { state: "thinking" } }));
  if (isReadySignal(question)) window.dispatchEvent(new CustomEvent("nova:reopen-save-card"));
  try {
    const turn = await askNova(question, voiceInput);
    target.textContent = turn.answer;
    window.dispatchEvent(new CustomEvent("nova:voice-state", { detail: { state: turn.intent === "human_handoff" ? "handoff" : "speaking", durationMs: 1800 } }));
  } catch (error) {
    target.textContent = error instanceof Error ? error.message : "Nova could not answer that question right now.";
    window.dispatchEvent(new CustomEvent("nova:voice-state", { detail: { state: "idle" } }));
  }
}

document.addEventListener("click", (event) => {
  const element = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-resource]") : null;
  if (!element) return;
  const question = resourceQuestions[element.dataset.resource ?? ""];
  if (!question) return;
  event.preventDefault(); event.stopImmediatePropagation(); void renderRuntimeConversation(question);
}, true);

document.addEventListener("submit", (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form || form.id !== "post-plan-question") return;
  const input = form.querySelector<HTMLInputElement>("#post-plan-input");
  const question = input?.value.trim() ?? "";
  if (!question) return;
  const voiceInput = consumeVoiceInputFlag();
  event.preventDefault(); event.stopImmediatePropagation(); input!.value = ""; void renderRuntimeConversation(question, voiceInput);
}, true);

initializeVisitorContinuity();
